import React, { useEffect, useState, useCallback } from 'react';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import { bookmarksAPI } from '../services/api';
import { BookmarkIcon, TrashIcon, BookOpenIcon, LanguageIcon, SparklesIcon } from '@heroicons/react/24/outline';

const TYPE_META = {
  lesson:         { label: 'Mësim',          icon: BookOpenIcon,  color: 'bg-blue-50 text-blue-700 border-blue-200' },
  grammar_topic:  { label: 'Temë Gramatike', icon: SparklesIcon,  color: 'bg-amber-50 text-amber-800 border-amber-200' },
  word:           { label: 'Fjalë',           icon: LanguageIcon,  color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
};

const TABS = ['Të gjitha', 'Mësim', 'Temë Gramatike', 'Fjalë'];
const TAB_TYPES = { 'Të gjitha': null, 'Mësim': 'lesson', 'Temë Gramatike': 'grammar_topic', 'Fjalë': 'word' };

const BookmarksPage = () => {
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('Të gjitha');
  const [removing, setRemoving]   = useState(null);

  const load = useCallback(async (type) => {
    setLoading(true);
    try {
      const res = await bookmarksAPI.list(type);
      setBookmarks(res.data.bookmarks || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(TAB_TYPES[activeTab]); }, [activeTab, load]);

  const handleRemove = async (id) => {
    setRemoving(id);
    try {
      await bookmarksAPI.remove(id);
      setBookmarks(prev => prev.filter(b => b.id !== id));
    } catch (e) { console.error(e); }
    setRemoving(null);
  };

  return (
    <DashboardLayout>
      <div className="page-shell-wide space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-yellow-100 flex items-center justify-center">
            <BookmarkIcon className="w-5 h-5 text-yellow-600" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-zinc-900">Të Ruajturat</h1>
            <p className="text-sm text-zinc-500">Mësimet, temat dhe fjalët e ruajtura</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 flex-wrap">
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all cursor-pointer ${
                activeTab === tab
                  ? 'bg-zinc-900 text-white border-zinc-400'
                  : 'bg-white text-zinc-600 border-zinc-200 hover:border-zinc-400'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* List */}
        {loading && (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="h-16 rounded-xl bg-zinc-100 animate-pulse" />
            ))}
          </div>
        )}

        {!loading && bookmarks.length === 0 && (
          <div className="text-center py-20 rounded-2xl border-2 border-dashed border-zinc-200">
            <BookmarkIcon className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
            <h2 className="font-semibold text-zinc-500">Asnjë i ruajtur</h2>
            <p className="text-sm text-zinc-400 mt-1">Kliko ikonën 🔖 kudo në aplikacion për të ruajtur.</p>
          </div>
        )}

        <div className="space-y-2">
          {bookmarks.map(bm => {
            const meta = TYPE_META[bm.content_type] || TYPE_META.lesson;
            const Icon = meta.icon;
            return (
              <div
                key={bm.id}
                className="flex items-center gap-3 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <div className={`w-9 h-9 rounded-lg border flex items-center justify-center shrink-0 ${meta.color}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-zinc-800 truncate">
                    {bm.content_title || bm.content_id}
                  </p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <span className="text-xs text-zinc-400">{meta.label}</span>
                    <span className="text-xs text-zinc-300">·</span>
                    <span className="text-xs text-zinc-400">
                      {new Date(bm.created_at).toLocaleDateString('sq-AL', { day: 'numeric', month: 'short' })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleRemove(bm.id)}
                  disabled={removing === bm.id}
                  className="w-8 h-8 rounded-lg flex items-center justify-center text-zinc-400 hover:text-red-500 hover:bg-red-50 transition-colors cursor-pointer disabled:opacity-40"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BookmarksPage;
